import { useLocation, useNavigate } from "react-router-dom";

import { InvoiceAddEditButtons } from "./InvoiceAddEditButtons";

import { MonthYearSelector } from "@/components/ui/v2/month-year-selector";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { ROUTES } from "@/constants/routes";

type InvoiceChannelTableToolbarProps = {
  onSearchChange: (value: string) => void;
  searchValue?: string;
  year: string;
  month: string;
  channelId: string;
  maxDate?: [string, string];
  currentPageLabel?: string;
  channelName?: string;
  isEditMode: boolean;
  onEditModeChange: (isEditMode: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditDisabled?: boolean;
  isSaving?: boolean;
  hasData?: boolean;
  onEditModeReset?: () => void;
};

const getSelectedTabId = (pathname: string): "toDo" | "invoiced" => {
  if (pathname.includes(`/${ROUTES.toDo}`)) {
    return "toDo";
  }
  if (pathname.includes(`/${ROUTES.invoiced}`)) {
    return "invoiced";
  }
  return "toDo";
};

export function InvoiceChannelTableToolbar({
  onSearchChange,
  searchValue,
  year,
  month,
  channelId,
  maxDate,
  currentPageLabel,
  channelName,
  isEditMode,
  onEditModeChange,
  onSave,
  onCancel,
  isEditDisabled = false,
  isSaving = false,
  hasData = false,
  onEditModeReset,
}: InvoiceChannelTableToolbarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleYearMonthChange = (yearMonth: [string, string]) => {
    // Reset edit mode before navigating
    onEditModeReset?.();
    
    const [newYear, newMonth] = yearMonth;
    const currentTab = getSelectedTabId(location.pathname);
    const tabRoute = currentTab === "toDo" ? ROUTES.toDo : ROUTES.invoiced;
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("page");
    const cleanSearch = searchParams.toString();
    const searchString = cleanSearch ? `?${cleanSearch}` : "";
    navigate(
      `/${ROUTES.invoices}/${ROUTES.channels}/${channelId}/${tabRoute}/${newYear}/${newMonth}${searchString}`,
      {
        state: {
          channelName: currentPageLabel ?? channelName,
        },
      }
    );
  };

  const currentTab = getSelectedTabId(location.pathname);

  return (
    <div className="flex flex-col w-full gap-4">
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          <InvoiceAddEditButtons
            isEditMode={isEditMode}
            onEditModeChange={onEditModeChange}
            onSave={onSave}
            onCancel={onCancel}
            isEditDisabled={isEditDisabled}
            isSaving={isSaving}
            hasData={hasData}
            tabType={currentTab}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-between">
        <TableToolbar
          onSearchChange={onSearchChange}
          searchValue={searchValue}
        />
        <MonthYearSelector
          value={[year, month]}
          onChange={handleYearMonthChange}
          maxDate={maxDate}
        />
      </div>
    </div>
  );
}
